import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RadioTower, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../../components/Button';
import { useAuth } from '../../store/authStore';
import { useSetup } from '../../store/setupStore';
import { getDeviceStatus } from '../../api/devices';
export function WaitingActivation() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const {
    deviceUid,
    expiresAt,
    labels,
    reset
  } = useSetup();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [isExpired, setIsExpired] = useState(false);
  // Countdown Timer
  useEffect(() => {
    if (!expiresAt) {
      // Fallback if expiresAt is missing
      setTimeLeft(30 * 60 * 1000);
      return;
    }
    const targetTime = new Date(expiresAt).getTime();
    const updateTimer = () => {
      const now = Date.now();
      const diff = targetTime - now;
      if (diff <= 0) {
        setTimeLeft(0);
        setIsExpired(true);
      } else {
        setTimeLeft(diff);
      }
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);
  // Polling
  useEffect(() => {
    if (isExpired || !deviceUid || !token) return;
    let isMounted = true;
    const poll = async () => {
      try {
        const res = await getDeviceStatus(token, deviceUid);
        if (res.activated && isMounted) {
          navigate('/app/setup/success');
        }
      } catch (err) {
        // Silently retry on network errors
      }
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [token, deviceUid, navigate, isExpired]);
  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  const handleTryAgain = () => {
    reset();
    navigate('/app/setup');
  };
  const handleCancel = () => {
    reset();
    navigate('/app/profile/devices');
  };
  return <main className="min-h-screen w-full bg-background md:bg-secondary flex flex-col items-center justify-center p-0 md:p-6 animate-fadeInUp" data-id="element-2224" data-mp="3or4okislsdqv">
      <div className="w-full max-w-[520px] min-h-screen md:min-h-[600px] bg-background md:rounded-card md:shadow-elevated flex flex-col relative overflow-hidden p-6" data-id="element-2225" data-mp="3tjpdp70ke2o2">
        <div className="flex flex-col items-center flex-1 justify-center pb-12 pt-12" data-id="element-2226" data-mp="6cnb2t1jhnym">
          {/* Sonar / Status Animation */}
          <div className="relative flex items-center justify-center w-[200px] h-[200px] mb-12" data-id="element-2227" data-mp="bdjbgwmomrza">
            {!isExpired ? <>
                {[0, 1, 2].map(i => <motion.div key={i} className="absolute inset-0 rounded-full border-2 border-primary" initial={{
              scale: 0.4,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: [0, 0.5, 0]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              delay: i * 1,
              ease: 'easeOut'
            }} data-id="element-2228" data-mp="2d5pgi3t3sr4i" />)}
                <div className="relative z-10 w-[80px] h-[80px] bg-primary rounded-full flex items-center justify-center shadow-elevated" data-id="element-2229" data-mp="1e5dqzvv8viqw">
                  <RadioTower size={36} className="text-white" data-id="element-2230" data-mp="1957yyel2bu8a" />
                </div>
              </> : <div className="relative z-10 w-[80px] h-[80px] bg-border rounded-full flex items-center justify-center shadow-sm" data-id="element-2231" data-mp="146ocmd2k4gfe">
                <XCircle size={36} className="text-text-secondary" data-id="element-2232" data-mp="3oxez0kl2i6l2" />
              </div>}
          </div>

          {isExpired ? <>
              <h1 className="text-[24px] font-semibold text-text-primary text-center mb-3" data-id="element-2233" data-mp="1gn5ui79p3om6">
                This code has expired
              </h1>
              <p className="text-[16px] text-text-secondary text-center max-w-[300px] mb-8" data-id="element-2234" data-mp="17oamqqr066gr">
                Your {labels.singular} didn't come online in time. Let's try
                again.
              </p>
              <div className="w-full max-w-[320px] space-y-4" data-id="element-2235" data-mp="20c6j1ucntwd0">
                <Button onClick={handleTryAgain} data-id="element-2236" data-mp="20alq295zulh9">Try Again</Button>
              </div>
            </> : <>
              <h1 className="text-[24px] font-semibold text-text-primary text-center mb-3" data-id="element-2237" data-mp="1qf1s7h7vdcpv">
                Powering on your {labels.singular}?
              </h1>
              <p className="text-[16px] text-text-secondary text-center max-w-[320px] mb-8" data-id="element-2238" data-mp="1vfpsyulwh7xa">
                Your {labels.singular} will link automatically once it's powered
                on and has a signal. This code expires in 30 minutes.
              </p>

              <div className="text-[48px] font-mono font-light text-text-primary tracking-wider mb-2" data-id="element-2239" data-mp="cdm7iurih8bp">
                {formatTime(timeLeft)}
              </div>
            </>}
        </div>

        {!isExpired && <div className="w-full mt-auto pt-6" data-id="element-2240" data-mp="ldj1nbwo6ze5">
            <button onClick={handleCancel} className="w-full text-center text-text-secondary font-medium text-[15px] py-2 hover:text-text-primary transition-colors" data-id="element-2241" data-mp="ggahj5ryola9">
              Cancel setup
            </button>
          </div>}
      </div>
    </main>;
}